import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import { Button, MenuItem } from "@mui/material";
import { IconButton } from "@mui/material";
import { ReactElement, useMemo } from "react";
import { useForm, useFieldArray, FieldValues } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import {
  MetadataEnumMultiFieldUISchema,
  MetadataFieldTypes,
} from "@stw/yup-validators";

import {
  MetadataEnumMultiFieldFragment,
  UpdateMetadataEnumMultiFieldMutation,
  UpdateMetadataEnumMultiFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import {
  createEmptySelectValue,
  handlePostSubmit,
} from "~frontend/components/campaignMetadata/common";
import Select from "~frontend/components/forms/FormSelect";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  UPDATE_METADATA_ENUM_MULTI_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type DefaultValueSelect = "Yes" | "No";

type UpdateEnumMultiFieldProps = {
  editField?: MetadataEnumMultiFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormInput = FieldValues & z.input<typeof MetadataEnumMultiFieldUISchema>;
type FormOutput = FieldValues & z.infer<typeof MetadataEnumMultiFieldUISchema>;

const EnumMultiFieldForm = FieldForm<FormInput>;

export function EditEnumMultiFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: UpdateEnumMultiFieldProps): ReactElement {
  const id = editField ? editField.id : undefined;

  const initialValues: FormInput = useMemo(() => {
    if (!editField) {
      return {
        name: initialName,
        required: false,
        defaultValue: [],
        values: [createEmptySelectValue()],
      };
    }
    const values = editField.configuration.values.map(v => {
      return {
        // create a unique key so if default selections are also unique.
        // otherwise, if we add two fields with the same text, set the default value
        // would also flip it for the matching row.
        key: uuidv4(),
        value: v,
      };
    });
    const { id, name, required, defaultEnumMultiValue } = editField;
    const defaultValue = values
      .filter(v => defaultEnumMultiValue.includes(v.value))
      .map(v => v.key);
    return {
      id,
      name,
      required,
      defaultValue,
      values,
    };
  }, [editField, initialName]);

  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormInput>({
    resolver: zodResolver(MetadataEnumMultiFieldUISchema),
    values: initialValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "values",
  });
  const defaultValue = watch("defaultValue");

  const [updateField] = useMutation<
    UpdateMetadataEnumMultiFieldMutation,
    UpdateMetadataEnumMultiFieldMutationVariables
  >(UPDATE_METADATA_ENUM_MULTI_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  async function onSubmit(values: FormOutput) {
    const {
      name,
      required,
      defaultValue: defaultsAsUUIDs,
      values: selectValues,
    } = values;
    // Look up uuid keys in fields for the field values.
    // It's possible there are duplicate values that output, I opted for the
    // api to remove duplicates.
    const defaultValue = selectValues
      .filter(f => defaultsAsUUIDs.includes(f.key))
      .map(f => f.value);
    const valuesWithoutKeys = selectValues.map(v => v.value);
    const { errors, data } = await updateField({
      variables: {
        input: {
          id,
          name,
          required,
          defaultValue,
          values: valuesWithoutKeys,
        },
      },
    });

    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataEnumMultiField,
      errors,
    });
  }

  function onFormCancelClick() {
    reset();
    onCancelClick();
  }

  function onDefaultValueChange(
    selectValue: string,
    newDefault: DefaultValueSelect
  ) {
    let newDefaultValues;
    if (newDefault === "Yes") {
      newDefaultValues = [...new Set([...defaultValue, selectValue])];
    } else if (newDefault === "No" && defaultValue.includes(selectValue)) {
      newDefaultValues = defaultValue.filter(v => v !== selectValue);
    } else {
      return;
    }
    setValue("defaultValue", newDefaultValues);
  }

  function addSelectOption() {
    append(createEmptySelectValue());
  }

  function removeSelectOption(index: number) {
    if (fields.length === 1) {
      setError("values.0.value", {
        message: "At least one option is required.",
      });
      return;
    }
    remove(index);
  }

  return (
    <EnumMultiFieldForm
      editField={editField}
      onTypeChange={onTypeChange}
      getValues={getValues}
      watch={watch}
      currentFieldType={MetadataFieldTypes.ENUM_MULTI}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
    >
      {fields.map((f, index) => {
        return (
          <FlexBox key={f.id} direction="column">
            <FlexBox direction="row" gap={2} sx={{ pl: 2 }}>
              <Select
                label="Default Value?"
                value={defaultValue.includes(f.key) ? "Yes" : "No"}
                onChange={e =>
                  onDefaultValueChange(
                    // Opted to use the key here as opposed to ID.
                    // The id is not available until after the call to
                    // rhf useForm()
                    f.key,
                    e.target.value as DefaultValueSelect
                  )
                }
                sx={{ minWidth: "25%" }}
              >
                <MenuItem value="No" key="No">
                  No
                </MenuItem>
                <MenuItem value="Yes" key="Yes">
                  Yes
                </MenuItem>
              </Select>
              <RhfTextField
                control={control}
                name={`values.${index}.value`}
                label={`Multi Select Option ${index + 1}`}
                style={{ flexGrow: 1 }}
                characterLimit={50}
              />
              <FlexBox alignItems={"center"}>
                <IconButton onClick={() => removeSelectOption(index)}>
                  <Delete />
                </IconButton>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        );
      })}
      <Button
        variant="contained"
        color="info"
        sx={{ maxWidth: "40%" }}
        onClick={addSelectOption}
      >
        + ADD MULTI SELECT OPTION
      </Button>
    </EnumMultiFieldForm>
  );
}
